/**
 * FigureReconvertModal — 그림 하나를 AI 에 다시 보내기 직전에 멈추는 창.
 *
 * 계약: docs/exam-builder-v2/BE-20260827-figure-editing-apis.md §3-3(OCR) · §4-3(은행)
 * 회신: docs/exam-builder-v2/BE-20260829-reply-fe-0828.md §4-1 — `user_prompt` 를 받는다
 *
 * ── 왜 공용 AiRunModal 을 쓰지 않나 (2026-08-30) ──
 * 이 자리에만 있는 것이 하나 있다: **AI 에 보낼 그림을 고르는 칸.**
 * 계약 §4-3 의 원본 우선순위는 `body.image` → 노드가 pixel 이면 그 src → 이력 보관 원본이고,
 * 셋 다 없으면 409 `no_original` 이다. OCR 에서 이미 도판이 되어 승격된 그림은 은행에
 * 보관 원본이 없어(§4-2 는 **은행 안에서의 첫 전환** 때만 보관한다) 그 409 가 확정이었다 —
 * 실측: 문제 61592 의 body/0 재요청이 56ms 만에 「재요청할 원본 그림이 없습니다」로 거절됐다.
 *
 * 공용 창에 그 칸을 더하지 않은 이유: 그림을 고르는 자리는 그림 재요청 하나이고,
 * AiRunModal 은 지시 칸만 있는 공용 부품으로 여섯 화면이 함께 쓴다. 한 자리 때문에
 * 공용 부품에 조건부 UI 를 넣으면 나머지 다섯이 그 조건을 읽어야 한다.
 *
 * ── 2026-08-31: 은행은 **늘** 그림을 실어 보낸다 (오너 지시) ──
 * 종전에는 보관 원본이 **없다고 확인된** 그림에만 화면 그림을 구워 보냈다(`needsImage`).
 * 그 판정은 `item.has_original` 하나에 걸려 있어, 서버가 그 키를 늦게 붙이거나 뜻을 바꾸면
 * 그대로 틀리고 틀린 값은 409 `no_original` 로 돌아왔다. 이제 은행은 늘 1순위를 채운다 —
 * 「요청할 때는 지금 보고 있는 이미지를 서버에 보내서 요청」(오너, 2026-08-31).
 *
 * **OCR 에는 보내지 않는다.** 계약 §3-3 의 body 에는 `image` 가 없고 서버가 교재 페이지에서
 * bbox 로 잘라 쓴다. 계약에 없는 키를 얹어 보내지 않는다 — 무시될지 400 일지 우리가 모른다.
 *
 * ── 무엇을 보낼지 사람이 고른다 ──
 * 도판이 된 그림에서 「지금 그림」을 보내면 AI 는 **한 번 옮겨진 결과**를 다시 옮긴다. 원본
 * 사진이 남아 있다면 그쪽이 더 나은 입력이다. 어느 쪽이 옳은지는 그림마다 다르므로 창이
 * 정하지 않고 **다 보여 주고 고르게 한다.**
 *
 * **기본은 [원본 사진]이다**(오너 결정 2026-09-01) — 원본 그림을 실제로 받은 그림에 한해서다.
 * 종전 기본은 「지금 그림」이었고, 그것이 계약 §4-3 의 1순위를 늘 채워 **보관 원본을 영영 쓰지
 * 않게 만들었다.** 그래서 재요청이 「직전 결과를 다시 옮기기」가 되어 버전이 수렴했다
 * (오너 보고 「버전별 이미지가 바뀌는 것도 없고 … 프롬프트가 반영되지도 않고」).
 * 2026-08-31 의 「보고 있는 이미지를 보내라」는 **업로드를 시키지 말라**는 뜻이었고, 그 요구는
 * [지금 그림] 카드가 남아 언제든 고를 수 있는 것으로 지켜진다.
 *
 * ── 2026-09-01: 고를 수 있는 것이 **넷**이 됐다 (오너 지시) ──
 * 「버전별로 선택할 수 있도록 해줘 — 현재 이미지와 원본만 보여주는 것이 아니라.」
 * 지금 그림 · 원본 사진 · **지난 버전(이력 v…)** · 올린 이미지. 지난 버전이 필요한 까닭은
 * 재요청이 늘 나아지지는 않기 때문이다 — v3 이 제일 나았는데 v5·v6 으로 나빠졌으면, 되돌려
 * 저장한 뒤 다시 요청하는 두 단계를 밟지 않고 **v3 을 그대로 재료로** 보낼 수 있어야 한다.
 * 카드 그림도 키웠다(56px → 96px) — 버전이 여럿이면 작은 썸네일로는 고를 수가 없다.
 *
 * ── 2026-09-01: 원본은 **올려 보내지 않고 가리킨다** (계약 §9-4 추가) ──
 * 종전에는 원본 사진도 캔버스로 구워(PNG 재인코딩) `body.image` 로 올려 보냈다. 이제
 * `use_original:true` 한 줄이면 서버가 **이력에 보관된 그 픽셀**로 강제 재생성한다.
 * 바뀌는 것 셋:
 *   ① 재료가 진짜 원본이다 — 캔버스를 거치며 한 번 다시 구워진 사본이 아니다.
 *   ② 굽지 못해도 고를 수 있다 — 원격 주소·캔버스 오염으로 굽기가 실패하면 종전에는 그
 *      카드가 통째로 사라져, 원본이 서버에 멀쩡히 있는데도 고를 길이 없었다.
 *   ③ 수백 KB 짜리 data-URI 를 올려 보내지 않는다.
 * 그래서 이 창이 굽는 것은 **「지금 그림」 하나**다. 원본 카드의 그림은 `original_src` 를
 * 그대로 건다(보기용이라 구울 이유가 없다) — 그것마저 없으면 이름만 있는 카드로 낸다.
 * 서버가 「없다」(`has_original:false`)고 말한 때만 카드를 빼고, **그때는 왜 없는지 한 줄로
 * 적는다**(2026-09-01 오너 지시). 카드도 없고 말도 없으면 사람은 화면이 고장 난 줄 안다.
 *
 * ── 지킬 것 넷 (AiRunModal 과 같은 규칙) ──
 * ① 지시는 **선택**이다. 비우고 보낼 수 있고, 비면 그 키를 아예 싣지 않는다.
 * ② **열 때마다 비운다.** 지난 요청의 지시·이미지가 다른 그림에 실리면 조용히 엉뚱한 것이 만들어진다.
 * ③ **보내는 중에는 닫히지 않는다.** 35~100초 동기 호출이라, 응답을 못 받고 닫히면
 *    새 `updated_at` 을 잃어 다음 저장이 409 로 잠긴다.
 * ④ 이 창은 그림 창 위에 겹쳐 뜬다 — 부모가 자기 ESC 를 꺼야 한다(FigureModal 의 closeOnEsc).
 *
 * ── 변환 모드 (계약 §9-4, 2026-08-31) ──
 * 「어떤 눈으로 옮길지」를 여기서 고른다. 고를 수 있는 것이 **자리마다 다르다** —
 * [사진 따라 그리기]는 문제은행 재요청에만 있다(figureText.figureModeChoices 에 그 규칙과 근거).
 * 기본값은 늘 「있는 그대로 옮기기」다: 고르지 않고 눌러도 종전과 같은 일이 일어나야 한다.
 */
import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Upload, X } from 'lucide-react';
import { Modal, Button, AiPromptInput } from '../common';
// 문구는 한 곳에 둔다 — 대상(OCR/은행)으로 갈리는 규칙이 거기 있다.
import {
  figureReconvertSummary, figureGenerateSummary, figureOriginalCardLabel, FIGURE_TRACE_RECONVERT_WARN,
  describeImageReject, figureModeChoices, figureRedrawInPlaceText, figureTraceLockTitle, figureMaterialDefaultNote,
} from './figureText';
import { FIGURE_MODE_DEFAULT, parseImageDataUri } from './figureApi';
// 모드 칸은 이 창이 유일하게 쓰는 자리가 됐다 — 부품은 그대로 둔다(다른 자리가 다시 생길 수 있다).
import FigureModePicker from './FigureModePicker';
import { FIGURE_SVG_EDITOR } from '../../utils/roles';
import { readStamp, isSvgFigure } from './svg/figureSvg';
// 화면에 보이는 그림을 그대로 보낼 수 있게 굽는다 — 사람에게 다시 올리라고 하지 않는다.
import toPngDataUri from './figureImage';

/** 서버가 받는 것은 data-URI 다(계약 §4-3). 원격 주소는 CORS 로 조용히 실패해 여기서 만들지 않는다. */
function readAsDataUri(file) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result));
    fr.onerror = () => reject(new Error('이미지를 읽지 못했습니다.'));
    fr.readAsDataURL(file);
  });
}

/** 고를 수 있는 재료의 이름 — 카드에 그대로 뜬다. 버전 카드와 [원본]은 pickLabel 이 짓는다. */
const PICK_LABEL = {
  text: '본문 글',
  screen: '지금 그림',
  crop: '교재 크롭',
  upload: '올린 이미지',
};

/**
 * 카드 키에서 버전 번호를 읽는다 — `'v:3'` → 3, 나머지는 null.
 *
 * 버전 카드에 별도 상태를 두지 않고 `pick` 한 값에 실어 두는 이유: 고를 수 있는 것이 넷이 됐는데
 * (지금 그림·원본·지난 버전·올린 것) 상태를 둘로 쪼개면 「버전을 골랐지만 pick 은 screen」 같은
 * 어긋난 조합이 만들어지고, 그러면 화면이 그리는 카드와 실제로 나가는 그림이 갈린다.
 */
function versionOf(key) {
  if (typeof key !== 'string' || !key.startsWith('v:')) return null;
  const n = Number(key.slice(2));
  return Number.isFinite(n) ? n : null;
}

function pickLabel(key, target, figureOrigins = []) {
  const n = versionOf(key);
  if (n != null) return `이력 v${n}`;
  if (key === 'original') return figureOriginalCardLabel(target);
  // 이 자리 그림의 원본 카드(2026-09-06) — 이름은 카드가 갖고 온다(「1번째 그림 원본」).
  const fig = figureOrigins.find((f) => f.key === key);
  if (fig) return fig.label;
  return PICK_LABEL[key] || '';
}

/**
 * ── 2026-09-05: **한 창이 생성과 재요청을 다 맡는다**(오너 지시 「어떤 경로든 동일 기능·동일 모달」) ──
 *
 * 종전에는 [AI 생성]이 공용 AiRunModal(지시 칸 + 모드)로, [AI 요청]이 이 창(재료 카드 + 모드 + 지시)으로
 * 열려 같은 「AI 로 그림 만들기」가 자리마다 다른 얼굴이었고, 캡처한 이미지로 새 그림을 만드는 길이
 * [AI 생성]에는 없었다. 이제 `kind` 하나로 가른다 —
 *   · `reconvert` (기본)  이 자리 그림을 다시 그린다. 재료: 원본 · 지금 그림 · 이력 v… · 올린 이미지
 *   · `generate`          그림을 **새로** 만들어 field 끝에 붙인다. 재료: 본문 글(기본) · 올린 이미지
 * 어느 쪽이든 모드 칸·지시 칸·[100초] 안내·버튼 자리가 같다. 다른 것은 카드 목록과 확인 버튼 이름뿐이다.
 *
 * ── 2026-09-06: 생성 얼굴에 **이 자리 그림들의 원본 카드**가 선다(오너 결정 A 안) ──
 * 「사진 따라 그리기」가 올린 이미지로만 열려 있었다(서버 생성 API 는 이미지를 안 받는다). 그런데 OCR 이 그림마다
 * 만들어 둔 원본(페이지 크롭)이 서버에 있다 — 오너 지적 「OCR 하면서 생성된 원본을 활용하거나 업로드한 이미지로」.
 * 그 원본은 **그림 단위**라, 카드도 그림마다 하나이고 고르면 **그 그림을 그 자리에서 다시 그린다**(재요청 —
 * [AI 요청]과 같은 호출, `source:'figure'`). 새 그림을 더하지 않는다는 점이 이 창의 머리말과 다르므로 고른 순간
 * 카드 아래에서 말하고(figureRedrawInPlaceText) 확인 버튼도 [다시 그리기]로 바뀐다. 카드는 호스트가 목록을 읽어
 * 넘긴다(figureOrigins.listFigureOrigins) — 이 창은 서버를 부르지 않는다.
 *
 * ── 2026-09-06: **[교재 크롭] 카드가 두 대상 공통이 됐다**(오너 지시 「검수 모달과 문제 편집의 과정이 동일해야」) ──
 * 종전에는 OCR 의 페이지 크롭을 [원본(서버 보관)] 카드의 **썸네일로만** 쓰고 아무것도 싣지 않았다. 교체(950)한
 * 그림에서는 서버가 올린 이미지를 먼저 보므로 카드가 보여 주는 것과 서버가 보는 것이 갈렸다(감사 G2). 이제 페이지
 * 크롭은 `originCropSrc` 로 들어와 은행의 `origin_crop` 과 같은 [교재 크롭] 카드가 되고 **바이트를 `image` 로 명시**한다.
 * [원본 사진] 카드는 보관 원본(`original_src`)이다. 기본은 보관 원본을 받았으면 그것, 아니면 교재 크롭,
 * 둘 다 없으면 지금 그림이다(defaultPick). (이날 낮까지 OCR 은 그 키를 못 받아 미리보기 없이 섰다 — 아래 저녁 절에서 닫혔다.)
 *
 * ── 2026-09-06 저녁: **[원본 사진]이 두 대상에서 같은 것을 보낸다** ──
 * 검수 항목에 `original_src` 가 실리고(회신 B) 재요청이 `use_original` 을 받으면서(회신 C), OCR 의 원본 카드도
 * 그림이 뜨고 `use_original:true` 로 나간다. 종전의 「아무것도 안 싣는 선택」은 서버 우선순위(자체 이미지 우선)에
 * 맡기는 것이라, 등록·교체한 그림에서 보관 원본이 아닌 것을 재료로 삼았다 — 그 갈래가 사라졌다.
 *
 * **OCR 도 카드를 낸다.** 종전에는 `target !== 'ocr'` 로 재료 고르기를 통째로 잠갔는데(계약 §3-3 에
 * `image` 가 없던 때의 판단), 2026-09-04 회신 §4 로 OCR 재요청도 `image` 를 받고 아무것도 안 보내면
 * 서버 보관 원본(페이지 크롭 또는 등록·교체한 이미지)을 쓴다. 그리고 2026-09-06 회신 C 로 검수 재요청도
 * `use_original` 을 받으면서 **두 대상이 같은 것을 보낸다** — 화면도, 나가는 것도 한 벌이다.
 */
export default function FigureReconvertModal({
  isOpen, target,
  kind = 'reconvert',
  // 생성일 때 어느 field 에 붙이는가 — 제목·요약이 「본문/풀이」를 말한다.
  field = 'body',
  // 지금 화면에 그려져 있는 그 그림(data-URI). 기본으로 이것을 구워 보낸다.
  screenSrc = null,
  /**
   * 크기를 **숫자 둘로 나눠 받는다.** 객체(`{width,height}`)로 받으면 부모가 리렌더될 때마다
   * 새 객체가 되어 아래 effect 의 의존성이 흔들리고, 그러면 **사람이 쓰던 지시가 지워진다**
   * (열 때마다 비우는 것이 ②이므로 effect 가 도는 것이 곧 비우는 것이다).
   * 그림 창은 미리보기 렌더 응답이 올 때마다 리렌더되므로 실제로 일어날 수 있는 일이다.
   */
  screenWidth = null, screenHeight = null,
  // 변환 전 원본 사진(계약 §1-2 `original_src`). 화면 그림과 다를 때만 후보로 낸다.
  // **보기용이다** — 보내는 것은 이 값이 아니라 `use_original:true` 다(위 머리주석).
  originalSrc = null,
  /**
   * 서버 쪽 원본 길이 열려 있는가 — `has_original === false` 일 때만 거짓이다
   * (figureText.figureOriginalPane 의 `serverHas`). **모르는 것으로 막지 않는다**:
   * 그림을 못 받았거나 `has_original` 키가 안 온 경우까지 잠그면, 서버에 멀쩡히 있는 원본을
   * 화면이 스스로 닫는다(2026-08-30 에 같은 실수로 [AI 요청]이 영영 잠긴 적이 있다).
   */
  originalKept = true,
  /**
   * 은행 승격 출신 그림의 **OCR 당시 지면 크롭**(계약 §10-2 G5 `origin_crop`, 2026-09-05). 보관 원본과 다른
   * 그림일 때 카드로 낸다 — 이미 PNG data-URI 라 굽지 않고 그대로 `image` 로 보낸다.
   */
  originCropSrc = null,
  /**
   * 원본이 지금 어떤 상태인지 한 줄 — 카드를 **내지 못할 때 그 자리에 적는다**(오너 지시
   * 2026-09-01 「원본이 삭제된 상태면 삭제됐다고 정확히 표출」). 종전에는 카드를 통째로 빼서,
   * 사람은 왜 [원본 사진]을 고를 수 없는지 알 길이 없었다 — 빈칸은 고장으로 읽힌다.
   */
  originalText = '',
  /**
   * **지난 버전 후보**(오너 지시 2026-09-01 「버전별로 선택할 수 있도록 — 현재 이미지와 원본만
   * 보여주는 것이 아니라」). `[{ version, src, alt }]` 로 받고, `src` 는 그 버전의 명세를 서버가
   * 그려 준 SVG data-URI 다(부모의 이력 썸네일을 그대로 쓴다 — 여기서 다시 렌더하지 않는다).
   *
   * **사진 버전은 오지 않는다.** 사진 버전이 돌아갈 곳은 서버가 보관한 원본 한 장뿐이라(§4-2)
   * 그것은 [원본 사진] 카드와 같은 것이 된다. 같은 그림에 다른 이름을 붙인 카드를 여러 장 내면
   * 고르는 사람이 차이를 찾느라 시간을 쓴다.
   *
   * `src` 가 아직 없는 버전(부모가 그리는 중)도 온다 — 자리를 비워 두면 목록이 들썩인다.
   *
   * ── 2026-09-06: **내용이 남은 판(등록·교체·트레이스)도 온다** ──
   * 좌표·식이 없어도 제 그림이 서버에 있고(계약 「판 내용 스냅샷」), 그것이 다시 그릴 재료다.
   * 그 판은 미리 받지 않으므로(「src 가 큼」) `needs:true` 로 오고, **고른 순간** 이 창이
   * `onNeedVersion(version)` 을 불러 부모가 1건만 받는다. 받는 동안은 `loading:true` 다.
   */
  versions = [],
  /** 그림이 아직 없는 버전 카드를 골랐을 때 부모에게 알린다(위 주석). 없으면 그런 카드는 눌리지 않는다. */
  onNeedVersion = null,
  // 이력을 읽는 중인가 — 카드가 하나도 없는 동안 화면이 아무 말도 하지 않으면 고장으로 읽힌다.
  versionsLoading = false,
  /**
   * 생성 얼굴에 낼 **이 자리 그림들의 원본 카드**(2026-09-06 A 안) — figureOrigins.listFigureOrigins 가 짓는다.
   * `[{ key, field, index, label, thumb, request }]`. 고르면 onConfirm 에 `source:'figure'` 와 `figure` 로 실려 나가고,
   * 호스트가 그 그림을 재요청한다. 이 창은 목록을 읽지 않는다 — 읽는 것은 호스트(openFigGen)다.
   */
  figureOrigins = [],
  // 호스트가 목록을 읽는 중인가 — 카드가 아직 없는 동안 화면이 아무 말도 안 하면 「원본은 못 고르는구나」로 읽힌다.
  figureOriginsLoading = false,
  initialMode = FIGURE_MODE_DEFAULT,
  /**
   * 지금 그림이 「사진 따라 그리기」로 만든 것인가(부모가 그림에서 판정 — figureTrace).
   *
   * 이 창이 그때 두 가지를 다르게 말한다 — ① 여기서 다시 그리면 **그림 속 글자가 원본 사진의
   * 것으로 되돌아간다**(오너가 하루에 여덟 번 눌러 확인한 것이 이것이다) ② 지금 그림은
   * 이력에서 되살릴 수 없다(사진 판은 카드로도 목록으로도 돌아갈 문이 없다).
   * **기본 모드는 바꾸지 않는다** — 기본 재료가 [원본 사진]이라, 모드까지 trace 로 해 두면
   * 창을 열고 한 번 누르는 것이 곧 「글자를 되돌리는」 동작이 된다. 모드는 아래 라디오에서 고른다.
   */
  traced = false,
  busy = false, onConfirm, onClose,
}) {
  const generating = kind === 'generate';
  const rawSvg = FIGURE_SVG_EDITOR && !!readStamp(screenSrc);
  const isOcr = target === 'ocr';
  const where = field === 'solution' ? '풀이' : '본문';

  const [prompt, setPrompt] = useState('');
  // 변환 모드(계약 §9-4). 열 때마다 기본으로 되돌린다 — 지난 그림에서 고른 [사진 따라 그리기]가
  // 다음 그림에 그대로 실리면, 그래프였을 것이 조용히 윤곽 그림이 된다.
  const [mode, setMode] = useState(initialMode);
  /* null = **아직 안 정했다.** 무엇이 기본인지는 재료를 아는 굽기 effect 가 정한다
     (원본을 받은 그림은 [원본 사진], 아니면 [지금 그림]). 여기서 못 박으면 「사람이 고른 것」과
     「기본값」을 구분할 수 없어, 재료가 갱신될 때마다 사람의 선택을 뺏게 된다. */
  const [pick, setPick] = useState(null);
  const usesCurrentSvg = rawSvg && pick === 'screen';   // null | 'text' | 'screen' | 'original' | 'crop' | 'upload' | 'v:{번호}' | 'fig:{field}/{index}'
  // 「지금 그림」을 PNG 로 구운 것. 원본은 굽지 않는다(위 머리주석 2026-09-01).
  const [screenBaked, setScreenBaked] = useState(null);
  const [baking, setBaking] = useState(false);
  /**
   * 지난 버전을 구운 것 — `{ [version]: dataUri | null }`. null 은 **굽기가 실패했다**는 뜻이고,
   * 키가 없으면 아직 굽지 않은 것이다. 둘을 갈라 두어야 「다시 눌러 볼 수 있는가」가 정해진다.
   *
   * 열 때 한꺼번에 굽지 않고 **고른 것만 굽는다.** 버전이 열 개면 캔버스를 열 번 돌리는데,
   * 실제로 보내는 것은 하나다. 굽기는 사용자가 카드를 누른 순간(choose)에 시작한다 —
   * effect 로 만들면 setState 가 곧 의존성 변화라 cleanup 이 돌고, 그러면 막 도착한 결과를
   * 버린다(부모의 thumbs effect 가 2026-08-30 에 실제로 그렇게 깨졌다).
   */
  const [verBaked, setVerBaked] = useState({});
  const [verBaking, setVerBaking] = useState(null);
  const [image, setImage] = useState(null);     // { name, dataUri } — 사람이 고른 것
  const [error, setError] = useState(null);
  const fileRef = useRef(null);
  // 생성 얼굴에서 고른 「이 자리 그림의 원본」 카드(2026-09-06 A 안) — 재요청 얼굴에는 없다.
  const pickedOrigin = (generating && typeof pick === 'string' && figureOrigins.find((f) => f.key === pick)) || null;
  // 사진이 있는가 — 올린 이미지를 골랐거나 그림 원본 카드를 골랐다. [사진 따라 그리기]는 이것이 있어야 열린다.
  const hasPhoto = (pick === 'upload' && !!image) || !!pickedOrigin;
  // 교재 크롭은 보관 원본과 **다른 그림일 때만** 카드가 된다 — 같은 그림에 이름을 둘 붙이지 않는다. 기본 재료 판정이 쓰므로 여기서 정한다.
  const cropSrc = !generating && originCropSrc && originCropSrc !== originalSrc ? originCropSrc : null;

  /**
   * ② **열 때 비운다.** 닫히는 도중이 아니라 열릴 때 비우는 이유는 AiRunModal 과 같다 —
   * 사라지는 창에서 값이 없어지면 글자가 깜빡인다.
   *
   * ⚠ **의존성은 `isOpen`·`initialMode` 둘뿐이다**(2026-09-01 에 굽기와 갈랐다).
   * 종전에는 비우는 일과 굽는 일이 한 effect 였고, 그래서 의존성에 `screenSrc`·`originalSrc`·
   * `originalKept`·크기 둘이 들어 있었다 — **창이 열려 있는 동안 재료가 갱신되면 사람이 쓰던
   * 지시가 통째로 지워졌다.** 크기를 객체가 아니라 숫자 둘로 쪼갠 것(위 prop 주석)도 같은 사고를
   * 막으려던 것인데, 그것은 「참조만 새로워지는」 경우만 막는다. 값 자체가 달라지는 경로 —
   * 부모가 이력을 읽어 item 을 병합하거나(2026-09-01 AI 창에서도 이력을 읽는다), 미리보기 렌더가
   * 도착해 화면 그림이 바뀌는 경우 — 는 그대로 뚫렸다.
   * **비우는 사건과 굽는 사건은 다른 사건이다.** 갈라 두면 재료가 바뀌어도 지시는 살아남는다.
   */
  useEffect(() => {
    if (!isOpen) return;
    /* initialMode 는 「거절당한 뒤 [사진 따라 그리기로 해보기] 로 다시 연 창」의 자리다 —
       그때 기본값으로 되돌리면 사용자가 방금 고른 길이 사라져 같은 거절을 또 맞는다. */
    setPrompt(''); setMode(initialMode); setImage(null); setError(null);
    /* **아직 정하지 않았다**(null). 무엇이 기본인지는 재료를 아는 아래 effect 가 정한다 —
       원본이 있는 그림과 없는 그림의 기본이 다르기 때문이다. 여기서 'screen' 으로 못 박아 두면
       아래에서 「사람이 고른 screen」과 「기본값 screen」을 구분할 수 없어, 재료가 갱신될 때마다
       사람이 고른 [지금 그림]을 도로 원본으로 뺏게 된다.
       생성은 재료가 둘뿐이고 굽는 것이 없어 여기서 바로 [본문 글]을 기본으로 둔다. */
    setPick(kind === 'generate' ? 'text' : null);
    // 구운 것도 비운다 — 다른 그림에서 구운 PNG 가 남아 있으면 조용히 엉뚱한 것이 나간다(②).
    setScreenBaked(null); setVerBaked({}); setVerBaking(null);
  }, [isOpen, initialMode, kind]);

  /**
   * 낼 수 있는 모드에 없는 값이 남지 않게 한다 — 지금은 어느 자리나 셋이라(2026-09-06) 실제로 되돌리는 일은 없지만,
   * 선택지 규칙(figureModeChoices)이 다시 좁아져도 라디오 없이 값만 서버로 나가는 일이 생기지 않게 남겨 둔다.
   */
  useEffect(() => {
    const where = kind === 'generate' ? 'generate' : (target === 'ocr' ? 'ocr-reconvert' : 'bank-reconvert');
    const allowed = figureModeChoices(where, { target, withImage: hasPhoto });
    if (!allowed.includes(mode)) setMode(FIGURE_MODE_DEFAULT);
  }, [kind, target, hasPhoto, mode]);

  /**
   * **재료를 굽는다** — 지시·모드는 건드리지 않는다(위 effect 와 갈라 둔 이유).
   *
   * **화면에 보이는 그림을 굽는다**(오너 지적 2026-08-30 「그림을 보고 AI 요청을 누른 건데
   * 원본을 업로드하라니?」). 계약 §4-3 의 1순위(`body.image`)를 사람 손 없이 채운다.
   * 원본은 굽지 않는다 — `use_original` 로 서버 보관본을 가리킨다(머리주석 2026-09-01).
   * 굽지 못하면(원격 주소·캔버스 오염 등) 원본이나 올리기로 물러난다.
   */
  useEffect(() => {
    // 생성에는 구울 그림이 없다 — 재료는 본문 글 아니면 올린 이미지다.
    if (!isOpen || generating) { setBaking(false); return undefined; }

    // 원본을 고를 수 있는가 — 같은 그림이면 카드를 둘로 내지 않는다(픽셀 그림은 원본이 곧
    // 지금 그림이다 · 계약 §1-2). 그림을 못 받았어도 서버가 보관하고 있으면 고를 수 있다 —
    // 두 대상 모두 보관 원본 그림을 받는다(회신 B) — 못 받은 판에서는 미리보기 없는 카드로 서고, 눌러도 서버가 제 보관본을 본다.
    const canOriginal = originalKept && (originalSrc ? originalSrc !== screenSrc : true);

    /**
     * **기본 재료는 보관 원본이다** (오너 결정 2026-09-01).
     *
     * 왜 뒤집었나: 계약 §4-3 의 재료 우선순위는 `body.image` → 픽셀 노드 src → 보관 원본이라,
     * 화면 도판을 늘 실으면 **보관 원본은 영영 쓰이지 않는다.** 그러면 재요청마다 「직전 결과를
     * 다시 옮기는」 일이 되어 v4·v5·v6 이 서로 구분되지 않고(오너 보고 2026-09-01 「버전별
     * 이미지가 바뀌는 것도 없고」), 이미 정리된 선 그림에는 지시가 물릴 대상도 적다.
     * 2026-08-31 의 「보고 있는 이미지를 보내라」는 **사람에게 업로드를 시키지 말라**는 지시였고,
     * 그 요구는 [지금 그림] 카드가 그대로 남아 언제든 고를 수 있는 것으로 충족된다.
     *
     * 단, **원본 그림을 실제로 받았을 때만** 기본으로 삼는다. `canOriginal` 은 서버가 보관 중이라
     * 말하기만 해도 참이 되는데(그림을 못 받아도 참), 그 경우까지 기본으로 두면 픽셀 그림에서
     * 409 `no_original` 이 기본 동작이 된다. 못 받았으면 종전대로 화면 그림이 기본이다.
     */
    /* 2026-09-06: 기본이 셋 중 하나다 — 보관 원본을 **그림으로 받았으면** 그것, 아니면 **교재 크롭**(OCR 당시 지면),
       둘 다 없으면 OCR 에 한해 미리보기 없는 보관 원본, 그것도 아니면 지금 그림. 아래 defaultKey 와 같은 식이다.
       같은 날 저녁 회신 B 로 **검수도 보관 원본 그림을 받으므로** 첫 갈래가 두 대상의 보통 경로가 됐다. 셋째 갈래
       (`isOcr && canOriginal`)는 서버가 「보관 중」이라고만 하고 그림도 크롭도 못 준 판에서만 서는 잔여 경로다 —
       은행에서는 이 상태에서 종전대로 지금 그림이 기본이고, 그 차이를 사람에게 figureMaterialDefaultNote 가 적어 준다.
       한쪽만 바꿔 두지 않는 이유: 이 상태를 dev 에서 만들 수 없어 바꾼 쪽을 확인할 방법이 없다. */
    const defaultPick = (canOriginal && originalSrc) ? 'original'
      : cropSrc ? 'crop'
        : (isOcr && canOriginal) ? 'original' : 'screen';
    /* 고른 것을 **뺏지 않는다.** 이 effect 는 재료가 바뀔 때마다 다시 도는데, 그때 사람이 이미
       카드를 골라 두었으면 그 선택이 정본이다. 정하는 것은 **아직 아무것도 안 골랐을 때**뿐. */
    setPick((cur) => cur ?? defaultPick);
    /* 화면 그림을 못 구웠을 때 물러날 곳 — 고를 수 없는 것이 골라져 있으면 [요청 보내기]가
       이유 없이 잠긴 것처럼 보인다. 사람이 일부러 고른 [지금 그림]도 여기서는 옮긴다
       (그 카드는 실제로 보낼 수 없는 상태다). */
    const fallback = () => setPick((cur) => ((cur == null || cur === 'screen')
      ? (defaultPick !== 'screen' ? defaultPick : (canOriginal ? 'original' : cropSrc ? 'crop' : 'upload')) : cur));

    let alive = true;
    if (!screenSrc) {
      setBaking(false);
      fallback();
      return undefined;
    }
    if (FIGURE_SVG_EDITOR && isSvgFigure(screenSrc)) {
      setBaking(false);
      if (!rawSvg) fallback();
      return undefined;
    }
    setBaking(true);
    toPngDataUri(screenSrc, { width: screenWidth || undefined, height: screenHeight || undefined })
      .catch(() => null)
      .then((uri) => {
        if (!alive) return;
        setScreenBaked(uri || null);
        setBaking(false);
        // 기본은 「지금 그림」이지만, 그것을 못 구웠으면 고를 수 있는 쪽으로 옮긴다 —
        // 고를 수 없는 것이 골라져 있으면 [요청 보내기]가 이유 없이 잠긴 것처럼 보인다.
        if (!uri) fallback();
      });
    return () => { alive = false; };
  }, [isOpen, generating, isOcr, screenSrc, screenWidth, screenHeight, originalSrc, originalKept, cropSrc, rawSvg]);

  const takeFile = async (file) => {
    if (!file) return;
    setError(null);
    if (!/^image\//.test(file.type)) { setError('이미지 파일만 올릴 수 있습니다.'); return; }
    try {
      const dataUri = await readAsDataUri(file);
      /**
       * 서버가 받아 주는 이미지인가 — 형식(png|jpeg|webp)과 디코드 2MB 를 **보내기 전에** 잰다
       * (계약 §10 · 회신 §7 은 형식 400, 초과 413). 재요청은 35~100초 동기 호출이라, 수 MB 를
       * 올려 보내고 그 시간을 기다린 뒤 거절당하면 사람이 잃는 것이 크다.
       */
      const check = parseImageDataUri(dataUri);
      if (!check.ok) { setError(describeImageReject(check)); return; }
      setImage({ name: file.name, dataUri });
      setPick('upload'); // 올린 사람은 그것을 보내려는 것이다 — 한 번 더 고르게 하지 않는다
    } catch (e) {
      setError(e.message || '이미지를 읽지 못했습니다.');
    }
  };

  // 붙여넣기로도 받는다 — 교재를 캡처해 바로 붙이는 것이 파일로 저장했다 고르는 것보다 짧다.
  const onPaste = (e) => {
    const it = [...(e.clipboardData?.items || [])].find((x) => /^image\//.test(x.type));
    if (!it) return;
    e.preventDefault();
    takeFile(it.getAsFile());
  };

  // 원본을 고를 수 있는가 — 판정은 위 effect 와 **같은 식**이다(둘이 어긋나면 카드는 뜨는데
  // 기본 선택이 엉뚱한 곳에 가 있거나, 그 반대가 된다).
  const canUseOriginal = !generating && originalKept && (originalSrc ? originalSrc !== screenSrc : true);
  /**
   * 창이 **실제로** [원본 사진]을 골라 두는가 — 위 굽기 effect 의 `defaultPick` 과 같은 식이다
   * (2026-09-01). 「고를 수 있는가(canUseOriginal)」와 「기본으로 골라지는가」는 다른 값이다:
   * 서버가 보관하고 있다고 말했지만 그림을 못 받은 그림에서는 카드는 떠도 기본은 [지금 그림]이다.
   * 두 문구가 그 차이를 모르고 「기본은 원본」이라 단언하면, 화면은 원본을 보냈다고 믿게 해 놓고
   * 실제로는 직전 결과를 다시 옮긴다 — 오너가 신고한 「여러 번 요청해도 안 달라진다」 그것이다.
   */
  // 위 defaultPick 과 **같은 식**이다(2026-09-06 셋으로) — 문구가 이 값으로 「기본은 무엇」을 말한다.
  const defaultKey = generating ? null
    : (canUseOriginal && originalSrc) ? 'original'
      : cropSrc ? 'crop'
        : (isOcr && canUseOriginal) ? 'original' : 'screen';
  /**
   * 원본을 고르면 **이미지를 싣지 않는다** — 은행은 `use_original:true`(계약 §9-4, 2026-09-01),
   * OCR 은 아무것도 싣지 않는다(회신 §4 — 서버 기본이 곧 보관 원본이다).
   * 그래서 이 갈래는 「보낼 그림」이 없어도 요청이 성립한다 — 아래 잠금 판정이 chosen 하나만
   * 보면 안 되는 이유다. 생성의 [본문 글]도 같은 성질이다(보낼 그림이 없는 것이 정상).
   */
  const usesOriginal = canUseOriginal && pick === 'original';
  const usesText = generating && pick === 'text';
  // 그림 원본 카드 — 보낼 그림을 이 창이 만들지 않는다(서버 보관본을 가리키거나, 카드가 가진 요청 그대로).
  const usesFigure = !!pickedOrigin;
  const pickedVersion = versionOf(pick);
  const pickedVer = pickedVersion != null ? versions.find((v) => Number(v.version) === pickedVersion) : null;
  /**
   * 그 판의 그림을 **굽지 않고 그대로 보낼 수 있는가** — 등록·교체 판은 이미 PNG 다(계약 §10 등록).
   * 서버가 받는 규격(png|jpeg|webp · 2MB)이면 캔버스를 거치지 않는다: 한 번 다시 굽는 만큼 화질이 깎이고,
   * jsdom·원격 주소처럼 캔버스가 없는 자리에서도 성립한다. 트레이스 판(SVG)만 굽는다.
   */
  const pickedVerDirect = pickedVer?.src && parseImageDataUri(pickedVer.src).ok ? pickedVer.src : null;

  /**
   * 고른 판을 서버에 보낼 수 있는 그림으로 만든다 — **그림이 손에 들어온 뒤**에 돈다(2026-09-06).
   * 내용이 남은 판은 고른 순간에는 `src` 가 없고(부모가 그때 받는다) 도착은 그다음 렌더다.
   *
   * ⚠ cleanup 을 두지 않는다 — 막 도착한 결과를 버리는 함정이 이 파일에 이미 두 번 있었다(위 주석들).
   * 결과는 버전 키로 담기고 버전은 불변이라(계약 §1-5) 늦게 온 것도 제 자리가 맞다.
   */
  useEffect(() => {
    if (!isOpen || pickedVersion == null || pickedVerDirect) return;
    if (pickedVersion in verBaked || verBaking === pickedVersion) return; // 이미 굽거나 굽는 중
    const src = pickedVer?.src;
    if (!src) return; // 아직 안 왔다 — 도착하면 이 effect 가 다시 돈다
    setVerBaking(pickedVersion);
    /* **크기를 반드시 함께 넘긴다.** figureImage 는 `naturalWidth || width` 가 0 이면 그대로
       거절하는데, SVG data-URI 는 그 안에 치수가 없으면 naturalWidth 가 0 으로 온다. */
    toPngDataUri(src, { width: pickedVer?.width || undefined, height: pickedVer?.height || undefined })
      .catch(() => null)
      .then((uri) => {
        setVerBaked((prev) => ({ ...prev, [pickedVersion]: uri || null }));
        setVerBaking((cur) => (cur === pickedVersion ? null : cur));
      });
  }, [isOpen, pickedVersion, pickedVerDirect, pickedVer, verBaked, verBaking]);
  // 실제로 나갈 그림. 고른 것이 비어 있으면 undefined 라 키가 실리지 않는다.
  const chosen = pick === 'upload' ? (image?.dataUri || null)
    : pick === 'screen' ? screenBaked
      : pick === 'crop' ? cropSrc
        : pickedVersion != null ? (pickedVerDirect || verBaked[pickedVersion] || null)
          : null;
  // 굽는 중인 것이 하나라도 있으면 「아직 재료가 없다」와 「보낼 것이 없다」를 가른다.
  const anyBaking = baking || verBaking != null;
  // 고를 수 있는 카드들 — 없는 것을 고르게 하지 않는다. 버전은 부모가 이미 걸러 넘긴다
  // (사진 버전 제외 · 지금 그림과 같은 현재 버전 제외 · 렌더가 실패한 버전 제외).
  const picks = generating
    ? ['text', ...figureOrigins.map((f) => f.key), image ? 'upload' : null].filter(Boolean)
    : [
      (screenBaked || rawSvg) ? 'screen' : null,
      canUseOriginal ? 'original' : null,
      cropSrc ? 'crop' : null,
      ...versions.map((v) => `v:${v.version}`),
      image ? 'upload' : null,
    ].filter(Boolean);
  // 서버에 줄 재료가 하나도 없다 — 그때만 409 `no_original` 이 확정이다.
  // 원본 카드(아무것도 안 보내도 성립)와 생성의 [본문 글]은 재료가 없는 것이 정상이라 막지 않는다.
  const nothingToSend = !generating && !usesCurrentSvg && !usesOriginal && !chosen && !anyBaking;
  /**
   * 생성에서 [사진 따라 그리기]를 골랐는데 사진이 없다 — 서버 생성 API 는 trace 를 받지 않는다(400).
   * 셋을 다 보이되(2026-09-06 오너 지시) 이때만 [만들기]를 잠그고, 무엇을 하면 되는지는 모드 칸 아래가 말한다.
   * 사진 = 올린 이미지 **또는** 이 자리 그림의 원본 카드(같은 날 A 안 — 그때는 그 그림을 그 자리에서 다시 그린다).
   */
  const traceNeedsImage = !FIGURE_SVG_EDITOR && generating && mode === 'trace' && !hasPhoto;

  /**
   * 카드를 고른다. **굽는 일은 여기서 하지 않는다**(2026-09-06) — 아래 effect 가 한다.
   * 까닭: 내용이 남은 판은 고른 **뒤에** 그림이 도착한다(onNeedVersion → 부모가 1건 받는다).
   * 굽기가 고르기와 한 몸이면 그때 구울 기회가 없어, 도착한 그림을 두고도 카드가 죽은 채로 남는다.
   *
   * ⚠ **실패한 것은 다시 누르면 다시 굽는다**(2026-09-01 규칙 유지). 실패도 `null` 로 키를 만들기
   * 때문에 아래 effect 의 「이미 처리함」 가드를 통과한다 — 그래서 여기서 그 키를 지워 다시 태운다.
   * 굽기 실패는 일시적일 수 있고(메모리·경합), 실제로 크기를 못 넘겨 실패하던 버그가 있었다.
   */
  const choose = (key) => {
    setPick(key);
    const n = versionOf(key);
    if (n == null) return;
    // 굽기에 실패했던 카드를 다시 눌렀다 — 표시를 지워 아래 effect 가 한 번 더 굽게 한다.
    if (verBaked[n] === null) setVerBaked((prev) => { const next = { ...prev }; delete next[n]; return next; });
    const ver = versions.find((v) => Number(v.version) === n);
    // 아직 손에 없는 판 — 부모에게 그 판 하나만 받아 오라고 한다(lazy · 계약 「src 가 큼」).
    if (!ver?.src && ver?.needs && typeof onNeedVersion === 'function') onNeedVersion(n);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => { if (!busy) onClose(); }}
      title={generating ? `${where} 그림 AI 생성` : 'AI 요청'}
      /* md(600px) → lg(800px). 카드가 커지고(96px) 지난 버전만큼 늘어나, 600px 에서는 한 줄에
         둘밖에 못 서고 목록이 세로로 길어진다. 고르는 것이 한눈에 들어와야 고를 수 있다. */
      size="lg"
      closeOnBackdropClick={false}
      closeOnEsc={!busy}
      showCloseButton={!busy}
      /* 값의 출처는 src/styles/z-index.css 의 --z-modal-ai-run 이다 — 그림 창 위에 겹쳐 뜨는 층을
         AiRunModal·FigureDeleteModal 과 함께 쓴다. 셋은 동시에 뜨지 않는다. */
      zIndex={9999986}
    >
      <div className="space-y-3" onPaste={onPaste}>
        <p className="text-sm leading-relaxed text-gray-700">
          {generating
            ? figureGenerateSummary(field, { withOrigins: figureOrigins.length > 0 })
            : figureReconvertSummary(target, { traced, defaultKey })}
        </p>

        {/* 트레이스 그림에서는 **이 창이 할 수 없는 일**을 맨 먼저 말한다 — 사람이 여기서 하려던
            그 일(그림 속 숫자 고치기)의 정본 경로가 다른 자리에 있고, 말하지 않으면 영영 못 찾는다.
            색은 danger 토큰 대신 amber 를 쓴다: 고장이 아니라 「자리를 잘못 찾았다」는 안내다. */}
        {!FIGURE_SVG_EDITOR && traced && (
          <p className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs leading-relaxed text-amber-800">
            {FIGURE_TRACE_RECONVERT_WARN}
          </p>
        )}

        {/* 어떤 눈으로 옮길지 (계약 §9-4). 자리마다 낼 수 있는 것이 다르다 — 그 규칙은
            figureText.figureModeChoices 가 갖고, 여기서는 어느 자리인지만 알려 준다. */}
        {!FIGURE_SVG_EDITOR && <FigureModePicker
          where={generating ? 'generate' : (isOcr ? 'ocr-reconvert' : 'bank-reconvert')}
          target={target}
          withImage={hasPhoto}
          withFigures={generating && figureOrigins.length > 0}
          value={mode}
          onChange={setMode}
          disabled={busy}
        />}

        {/* AI 가 받을 재료. 무엇이 나가는지 **그림으로** 보여 준다: 글로 「화면의 그림을
            보냅니다」라고만 하면, 그림이 둘인 창에서 어느 것인지 알 수 없다.
            OCR 도 같은 카드를 낸다(2026-09-05) — [원본(서버 보관)]은 아무것도 싣지 않는 선택이다. */}
        <div className="space-y-2 rounded-xl border border-gray-200 bg-gray-50 p-3">
          <p className="text-xs font-medium text-gray-700">{generating ? 'AI 가 볼 재료' : 'AI 가 받을 그림'}</p>

          {/* 조건이 `picks.length === 0` 이 아닌 이유(2026-09-01): 원본 카드는 굽지 않으므로
              굽는 동안에도 이미 떠 있다. 그러면 카드가 하나 있는데 아무것도 골라져 있지 않은
              («지금 그림»이 골라져 있으나 그 카드가 아직 없다) 몇 백 ms 가 생기고, 화면은 그
              동안 아무 말도 하지 않는다. 「지금 그림이 아직 없다」를 그대로 말한다. */}
          {baking && !screenBaked && (
            <div className="flex items-center gap-2">
              <span className="size-14 shrink-0 animate-pulse rounded bg-gray-200" aria-hidden="true" />
              <p className="text-xs text-gray-600">[지금 그림]을 준비하는 중입니다…</p>
            </div>
          )}

          {/* 이력을 아직 읽는 중 — 카드가 둘뿐인 화면에 아무 말이 없으면 「버전은 못 고르는구나」로
              읽힌다. 도착하면 카드가 늘어난다는 사실을 그동안 자리로 말한다. */}
          {versionsLoading && (
            <p className="text-[11px] text-gray-500">지난 버전을 읽는 중입니다 — 도착하면 카드가 늘어납니다.</p>
          )}
          {generating && figureOriginsLoading && (
            <p className="text-[11px] text-gray-500">{where}의 그림 원본을 읽는 중입니다 — 도착하면 카드가 늘어납니다.</p>
          )}

          {picks.length > 0 && (
            /* 그림을 키웠다(오너 지시 2026-09-01 「이미지들을 좀 더 확대해서」) — 56px → 96px.
               카드가 늘어날 수 있으므로 넘치는 만큼은 이 칸이 스크롤한다: 창이 세로로 길어지면
               아래 [지시]와 [요청 보내기]가 화면 밖으로 밀려난다. */
            <div className="flex max-h-80 flex-wrap gap-2 overflow-y-auto">
              {picks.map((key) => {
                const n = versionOf(key);
                const ver = n == null ? null : versions.find((v) => Number(v.version) === n);
                // 원본 카드의 그림은 **굽지 않은 `original_src` 그대로**다 — 보기용이라
                // 구울 이유가 없고, 그것마저 못 받았으면 이름만 있는 카드로 낸다.
                const fig = figureOrigins.find((f) => f.key === key) || null;
                const src = key === 'upload' ? image?.dataUri
                  : key === 'screen' ? (rawSvg ? screenSrc : screenBaked)
                    : key === 'original' ? originalSrc
                      : key === 'crop' ? cropSrc
                        : fig ? (fig.thumb || null)
                          : ver?.src || null;
                const on = pick === key;
                // 버전 카드만 갖는 상태들 — 아직 그림이 없다 · 받는 중이다 · 여기서 굽다 실패했다.
                const drawing = n != null && !ver?.src;
                // 누르면 부모가 받아 오는 판(내용 판) — 그림이 없어도 죽은 카드가 아니다.
                const fetchable = n != null && !ver?.src && (ver?.needs || ver?.loading);
                const failed = n != null && verBaked[n] === null;
                const label = pickLabel(key, target, figureOrigins);
                // 그림이 없는 카드의 자리글 — [본문 글]은 그림이 아니고, OCR 의 [원본]·그림 원본 카드는 미리보기(페이지
                // 크롭)를 못 받았어도 서버는 갖고 있다. 「미리보기 없음」으로 뭉개면 고를 수 없는 것으로 읽힌다.
                const placeholder = key === 'text' ? `${where}의 글로 새로 짓습니다`
                  : (key === 'original' || fig) ? '서버가 보관한 원본(미리보기 없음)'
                    : ver?.loading ? '받는 중…'
                      : ver?.needs ? '누르면 받아옵니다'
                        : drawing ? '그리는 중…' : '미리보기 없음';
                return (
                  <button
                    key={key}
                    type="button"
                    aria-pressed={on}
                    disabled={busy || (drawing && !fetchable)}
                    onClick={() => choose(key)}
                    title={ver?.alt || label}
                    className={`flex w-32 flex-col items-center gap-1 rounded-lg border p-2 disabled:opacity-50 ${
                      on ? 'border-primary bg-white ring-1 ring-primary' : 'border-gray-200 bg-white hover:border-gray-300'}`}
                  >
                    {src ? (
                      <img src={src} alt={label} className="size-24 object-contain" />
                    ) : (
                      <span className="flex size-24 items-center justify-center rounded bg-gray-100 px-1 text-center text-[10px] leading-tight text-gray-400">
                        {placeholder}
                      </span>
                    )}
                    <span className={`text-[11px] ${on ? 'font-bold text-primary' : 'text-gray-500'}`}>
                      {label}
                    </span>
                    {/* 고른 것이 굽기에 실패했으면 **그 자리에서 말한다** — 아래 버튼만 잠기면
                        왜 잠겼는지 알 길이 없다. 굽는 중인 것도 같은 자리에서 말한다. */}
                    {failed && <span className="text-[10px] text-danger">보낼 수 없음</span>}
                    {n != null && verBaking === n && <span className="text-[10px] text-gray-400">준비 중…</span>}
                  </button>
                );
              })}
            </div>
          )}

          {/* 그림 원본 카드를 골랐다 — 이 창의 머리말(맨 끝에 더한다)과 다르게 움직이는 유일한 갈래라 그 자리에서 말한다. */}
          {pickedOrigin && (
            <p className="text-[11px] leading-relaxed text-gray-600">
              <b>{pickedOrigin.label}</b>을 보고 {figureRedrawInPlaceText(pickedOrigin.index)}
            </p>
          )}

          {/* 버전 카드가 무엇인지, 그리고 **왜 사진 버전이 거기 없는지** 한 줄로 짚는다.
              이력 화면에는 사진 줄이 여럿인데 여기엔 없으니, 말하지 않으면 빠진 것으로 읽힌다. */}
          {/* 카드가 하나도 없을 때 **왜 없는지** 말한다 — 빈칸은 고장으로 읽히고, 그러면
              「버전을 못 고른다」는 보고가 된다. 사유가 둘이라 둘 다 적는다. */}
          {!FIGURE_SVG_EDITOR && !generating && !versionsLoading && versions.length === 0 && (
            <p className="text-[11px] leading-relaxed text-gray-500">
              고를 지난 버전이 없습니다 — 좌표·식 버전이 지금 것 하나뿐이거나, 이 그림이 아직 사진입니다.
              (사진 버전은 [{figureOriginalCardLabel(target)}] 카드 하나로 모입니다.)
            </p>
          )}

          {!generating && versions.length > 0 && (
            <p className="text-[11px] leading-relaxed text-gray-500">
              [이력 v…]를 고르면 <b>그 버전의 그림</b>을 재료로 보냅니다. 사진 버전은 카드로 내지 않습니다 —
              돌아갈 곳이 서버가 보관한 원본 한 장뿐이라 [{figureOriginalCardLabel(target)}]과 같은 것이 됩니다.
            </p>
          )}

          {/* 무엇이 기본으로 나가는지 — 기본 카드가 원본·교재 크롭·지금 그림 셋 중 무엇인지에 따라 한 줄(figureMaterialDefaultNote).
              「기본은 원본」이라 단언하는 문장이라 카드를 낼 수 있다는 것만으로는 내지 않는다(2026-09-01) — 창이 실제로 골라 두는
              것(defaultKey)으로 가른다. 말하지 않으면 사람은 원본이 나간다고 믿고 여러 번 눌러 본다. */}
          {!generating && figureMaterialDefaultNote(defaultKey, { target, canUseOriginal }) && (
            <p className="text-[11px] leading-relaxed text-gray-500">
              {figureMaterialDefaultNote(defaultKey, { target, canUseOriginal })}
            </p>
          )}

          {/* 원본을 고를 수 없을 때 — **왜 없는지 여기서 말한다**(오너 지시 2026-09-01).
              카드를 내지 않는 것 자체는 이 저장소의 규칙이지만(눌리면 반드시 실패하는 버튼을
              내지 않는다), 그러면서 아무 말도 하지 않으면 사람은 원본이 있는데 화면이 고장 난
              것으로 읽는다. 원본이 곧 지금 그림이라 카드를 하나만 낸 경우에는 할 말이 없다
              (그때 `originalText` 가 빈 문자열이다). */}
          {!generating && !canUseOriginal && originalText && (
            <p className="text-[11px] leading-relaxed text-amber-700">{originalText}</p>
          )}

          {nothingToSend && (
            <p className="text-xs leading-relaxed text-amber-800">
              <b>화면의 그림을 보낼 수 없었습니다</b> — 이미지를 올려 주세요. 안 올리면 AI 가 읽을 것이 없어 거절됩니다.
            </p>
          )}

          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => { takeFile(e.target.files?.[0]); e.target.value = ''; }}
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="xs"
              variant="secondary"
              icon={<Upload className="size-3.5" />}
              disabled={busy}
              onClick={() => fileRef.current?.click()}
            >
              {image ? '다른 이미지로 바꾸기' : '이미지 올리기'}
            </Button>
            <span className="text-[11px] text-gray-500">또는 이 창에 바로 붙여넣기(⌘/Ctrl+V)</span>
            {image && (
              <button
                type="button"
                aria-label="올린 이미지 빼기"
                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
                disabled={busy}
                onClick={() => {
                  setImage(null);
                  // 빼면 고른 것이 사라진다 — 고를 수 있는 쪽으로 되돌린다. 둘 다 없으면
                  // 'upload' 에 남겨 둔다: 그때 아래 안내가 「이미지를 올려 달라」고 말한다.
                  // 물러나는 순서도 기본과 같다 — 원본이 먼저다(위 defaultPick 의 근거).
                  if (pick === 'upload') {
                    setPick(generating ? 'text'
                      : (defaultKey !== 'screen' ? defaultKey : ((screenBaked || rawSvg) ? 'screen' : 'upload')));
                  }
                }}
              >
                <X className="size-3.5" />
              </button>
            )}
          </div>
          {image && (
            <p className="truncate text-[11px] text-gray-500" title={image.name}>{image.name}</p>
          )}
          {error && <p role="alert" className="text-[11px] text-danger">{error}</p>}
        </div>

        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs leading-relaxed text-amber-800">
          그림을 만드는 동안에는 이 창을 닫을 수 없습니다.
        </div>

        <AiPromptInput
          label="AI 에 보낼 지시(선택)"
          value={prompt}
          onChange={setPrompt}
          rows={3}
          placeholder="예: 점선은 무시하라 · 작은 원은 8개다 · 축은 그리지 마라"
          disabled={busy}
        />

        <div className="flex justify-end gap-2">
          <Button size="sm" variant="secondary" onClick={onClose} disabled={busy}>취소</Button>
          <Button
            size="sm"
            loading={busy}
            /* 서버에 줄 재료가 하나도 없을 때만 막는다 — 그때는 409 `no_original` 이 확정이다.
               「눌리면 반드시 실패하는 버튼을 내지 않는다」(figureText.figureCapabilities)와 같은 규칙.
               원본을 고른 경우는 보낼 그림이 없어도 성립한다(use_original — 위 usesOriginal). */
            disabled={busy || nothingToSend || traceNeedsImage || (!usesCurrentSvg && !usesOriginal && !usesText && !usesFigure && !chosen)}
            title={traceNeedsImage
              ? figureTraceLockTitle(figureOrigins.length > 0)
              : !usesCurrentSvg && !usesOriginal && !usesText && !usesFigure && !chosen
                ? (anyBaking ? '보낼 그림을 준비하는 중입니다'
                  : (pickedVersion != null && verBaked[pickedVersion] === null
                    ? '이 버전을 이미지로 바꾸지 못했습니다 — 다른 카드를 고르거나 이미지를 올려 주세요.'
                    : '보낼 이미지를 올려야 요청할 수 있습니다'))
                : ''}
            onClick={() => onConfirm({
              userPrompt: prompt.trim() ? prompt : undefined,
              // 원본·본문 글을 골랐으면 이미지를 싣지 않는다 — 서버가 무시하는 값을 올려 보내면
              // 요청 본문이 「무엇을 보고 그렸나」를 거짓으로 말한다(buildReconvertPayload).
              image: (usesCurrentSvg || usesOriginal || usesText || usesFigure) ? undefined : (chosen || undefined),
              // [원본 사진]은 **두 대상 모두** `use_original:true` 다(2026-09-06 회신 · dev `92c98ef`).
              // 종전에는 OCR 이 그 키를 못 받아 「아무것도 안 싣는 선택」으로 흉내 냈는데, 그러면 서버 우선순위가
              // 자체 이미지를 먼저 잡아 등록·교체한 그림에서 보관 원본이 아닌 것을 보고 그렸다. 이제 지정한다.
              useOriginal: usesOriginal || undefined,
              mode: FIGURE_SVG_EDITOR ? undefined : mode,
              // 생성일 때 **무엇으로** 만드는가 — 본문 글(generate)인지, 올린 이미지(등록 뒤 재요청)인지,
              // 이 자리 그림의 원본(그 그림을 그 자리에서 재요청 — 2026-09-06 A 안)인지.
              source: generating ? (pickedOrigin ? 'figure' : pick === 'upload' ? 'upload' : 'text') : undefined,
              // 원본 카드를 골랐을 때 **어느 그림을, 무엇을 실어** 재요청하는가 — 카드가 가진 것을 그대로 넘긴다.
              figure: pickedOrigin
                ? { field: pickedOrigin.field, index: pickedOrigin.index, request: pickedOrigin.request || {}, label: pickedOrigin.label }
                : undefined,
              /* **서버로 나가지 않는다.** 응답에는 「무엇을 보고 그렸는지」가 없어(계약 §9-4)
                 화면이 그 말을 해야 하는데, 고를 수 있는 것이 넷이 된 뒤로는 `useOriginal`
                 하나로 갈리지 않는다. 결과 토스트가 이 이름을 그대로 쓴다. */
              sourceLabel: pickLabel(pick, target, figureOrigins),
            })}
          >
            {generating ? (pickedOrigin ? '다시 그리기' : '만들기') : '요청 보내기'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

FigureReconvertModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  target: PropTypes.oneOf(['ocr', 'bank']),
  // 'reconvert' 이 자리 그림을 다시 그린다 · 'generate' 그림을 새로 만들어 field 끝에 붙인다(머리주석 2026-09-05).
  kind: PropTypes.oneOf(['reconvert', 'generate']),
  // 생성일 때 붙일 자리 — 제목·요약이 「본문/풀이」를 말한다.
  field: PropTypes.oneOf(['body', 'solution']),
  // 화면에 보이는 그 그림(data-URI). 은행에서는 기본으로 이것을 구워 보낸다.
  screenSrc: PropTypes.string,
  screenWidth: PropTypes.number,
  screenHeight: PropTypes.number,
  // 변환 전 원본 사진(data-URI). 화면 그림과 다를 때만 고를 수 있는 후보가 된다.
  originalSrc: PropTypes.string,
  // 서버 쪽 원본 길이 열려 있나(figureOriginalPane 의 serverHas) — 그림을 못 받았어도 고를 수 있다.
  originalKept: PropTypes.bool,
  // 원본이 없을 때 그 자리에 적을 한 줄. 있을 때는 빈 문자열이다.
  originalText: PropTypes.string,
  // 은행 승격 출신 그림의 OCR 당시 지면 크롭(data-URI) — 보관 원본과 다를 때 [교재 크롭] 카드가 된다.
  originCropSrc: PropTypes.string,
  /* 지난 버전 후보 — src 는 그 버전을 서버가 그려 준 SVG data-URI(부모의 이력 썸네일).
     아직 안 그려진 버전은 src 없이 온다(자리를 비워 두면 목록이 들썩인다). */
  versions: PropTypes.arrayOf(PropTypes.shape({
    version: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    src: PropTypes.string,
    // 서버 렌더가 준 도판 치수 — 굽기에 반드시 필요하다(figureImage 는 0 이면 거절한다).
    width: PropTypes.number,
    height: PropTypes.number,
    alt: PropTypes.string,
    // 내용이 남은 판 — 아직 안 받았다(needs) · 받는 중이다(loading). 고르면 onNeedVersion 이 돈다.
    needs: PropTypes.bool,
    loading: PropTypes.bool,
  })),
  onNeedVersion: PropTypes.func,
  versionsLoading: PropTypes.bool,
  // 생성 얼굴의 「이 자리 그림들의 원본」 카드(figureOrigins.listFigureOrigins) — 고르면 그 그림을 그 자리에서 다시 그린다.
  figureOrigins: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    field: PropTypes.oneOf(['body', 'solution']).isRequired,
    index: PropTypes.number.isRequired,
    label: PropTypes.string.isRequired,
    thumb: PropTypes.string,
    request: PropTypes.object,
  })),
  figureOriginsLoading: PropTypes.bool,
  // 창을 열 때 골라져 있을 모드. 거절 뒤 [사진 따라 그리기로 해보기] 로 다시 열 때 쓴다.
  initialMode: PropTypes.oneOf(['faithful', 'trace', 'schematic']),
  // 지금 그림이 「사진 따라 그리기」로 만든 것인가 — 위 prop 주석에 그때 달라지는 말 둘이 있다.
  traced: PropTypes.bool,
  busy: PropTypes.bool,
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};
